import Geometry from 'ol/geom/geometry'
import loadingStrategy from 'ol/loadingstrategy'
import tileGrid from 'ol/tilegrid'
import VectorLayer from 'ol/layer/vector'
import Collection from 'ol/collection'
import VectorSource from 'ol/source/vector'
import GlonassJSONFormat from '../../Format/OL3/GlonassJSSON'
import GeoJSONFormat from 'ol/format/geojson'
import WFSFormat from 'ol/format/wfs'
import GML2Format from 'ol/format/gml2'
import GML3Format from 'ol/format/gml3'
import Feature from 'ol/feature'
import Observable from 'ol/observable'
import uuid from 'node-uuid'
import 'Util.js'
import 'System/Promises'
import 'Data/Cache'
import './Base'
import '../../../Format'

/** @namespace GIS.Map.Layer.OL3 */
NS("GIS.Map.Layer.OL3");

/**
 * @class
 * @extends {GIS.Map.Layer.OL3.Base}
 * @extends {System.Promises}
 */
GIS.Map.Layer.OL3.Vector = Class(GIS.Map.Layer.OL3.Base, System.Promises, /** @lends GIS.Map.Layer.OL3.Vector.prototype */{
    /**
     * @type {ol.layer.Vector|ol.layer.Image}
     * @protected
     */
    layer: null,
    /**
     * Base layer should be of Vector type
     * @type {GIS.Map.Layer.Vector}
     * @protected
     */
    base: null,
    /**
     * @type {ol.source.Vector}
     * @protected
     */
    source: null,
    /**
     * @type {ol.source.Cluster}
     * @protected
     */
    clusterSource: null,
    /**
     * @protected
     * @type {ol.format.Feature}
     */
    format_: null,
    /**
     * @protected
     * @type {ol.format.GeoJSON}
     */
    geoJSONFormat: null,
    /**
     * @type {ol.style.StyleFunction}
     */
    styleFunction: null,
    //options
    /** @type {string|number} */
    format: GIS.Format.GeoJSON,
    /** @type {glStyleHash} */
    style: null,
    geometryName: "geometry",
    dataGeometryName: "geojson",
    dataPrimaryKeyName: "id",
    dataGeometryNameMap: undefined,
    url: "",
    wrapX: true,
    loadingStrategy: "tile",
    tileSize: [1024, 1024],
    ratio: 1.5,
    clusterDistance: null,
    asImage: false,
    /**
     * @type {ol.FeatureLoader}
     */
    loader: null,
    runningRequestsNum_: 0,
    /**
     * @param {GIS.Map.OL3} map
     * @param {GIS.Map.Layer.Vector} base
     * @param {Object} options
     */
    initialize(map, base, options)
    {
        this.Parent(map, base, options);

        // create GeoJSON format reader, used in insert/update and other inner methods
        this.geoJSONFormat = new GeoJSONFormat(_.assign({
            defaultDataProjection: this.projection
        }, Util.pickOptions(this, ["geometryName"])));
    },
    /**
     * @returns {GIS.Map.Layer.OL3.Vector}
     */
    destroy()
    {
        delete Data.Cache.values[this.name + 'styles']
        this.cancelAllPromises();
        this.clear();

        this.source.un("addfeature", this.onSourceAddFeature, this);
        this.source.un("removefeature", this.onSourceRemoveFeature, this);

        if (this.clusterSource) {
            this.clusterSource.un("addfeature", this.onSourceAddFeature, this);
            this.clusterSource.un("removefeature", this.onSourceRemoveFeature, this);
        }

        if (this.map) {
            this.map.off("feature.select", this.onFeatureSelect, this)
                .off("feature.unselect", this.onFeatureUnSelect, this);
        }

        this.style = null;
        this.format_ = null;
        this.loader = null;
        this.geoJSONFormat = null;
        this.styleFunction = null;
        this.clusterSource = null;

        return this.ParentCall();
    },
    /** @inheritDoc */
    optionsNames()
    {
        return this.ParentCall().concat([
            'format', 'style', 'styleFunction', 'geometryName', 'dataGeometryName', 'dataGeometryNameMap',
            'dataPrimaryKeyName', 'url', 'wrapX', 'loadingStrategy', 'tileSize', 'ratio',
            'clusterDistance', 'loader', 'asImage'
        ])
    },
    /**
     * @param {Object} options
     * @protected
     */
    prepareOptions(options)
    {
        options = this.Parent(options);
        options.style = this.map.getStyle(options.style)

        if (options.dataGeometryNameMap) {
            const geomFunc = feature => {
                const zoomPoints = Object.keys(this.dataGeometryNameMap).map(parseFloat).sort((a, b) => a - b)
                const zoom = zoomPoints.find(x => x > this.map.getZoom())
                if (zoom == null) return feature.getGeometry()

                const geomName = this.dataGeometryNameMap[zoom]
                let geom = feature[geomName]
                if (!(geom instanceof Geometry)) {
                    geom = feature[geomName] = this.format_.readGeometry(feature.get(geomName), {
                        featureProjection: this.map.getProjection(),
                        dataProjection: this.projection
                    })
                }

                return geom
            }
            options.style = _.mapValues(options.style, style => {
                if (style && !_.isEmpty(style.styles)) {
                    style.styles[0].geometry = geomFunc
                }
                return style
            })
        }
        options.styleFunction = this.createStyleFunction(options.style, options.name + 'styles');

        if (options.tileSize != null && !_.isArray(options.tileSize)) {
            options.tileSize = [_.parseInt(options.tileSize)];
        }

        return options;
    },
    /**
     * @param {string} strategyName
     * @param {Object} [options]
     * @returns {ol.LoadingStrategy}
     * @protected
     */
    prepareLoadingStrategy(strategyName, options)
    {
        let strategy = (/** @type {ol.LoadingStrategy} */loadingStrategy[ strategyName ] || loadingStrategy.all);

        if (strategyName === 'tile') {
            strategy = strategy(tileGrid.createXYZ({
                extent: this.map.getProjection().getExtent(),
                maxZoom: Util.coalesce(options.maxZoom, this.map.maxZoom),
                minZoom: Util.coalesce(options.minZoom, this.map.minZoom),
                tileSize: Util.coalesce(options.tileSize, [256, 256])
            }));
        }

        return strategy;
    },
    /**
     * @fires GIS.Map.Layer.OL3.Vector#before.load
     * @param {Object} [options]
     * @protected
     */
    beforeLoad(options = {})
    {
        if (!this.runningRequestsNum_) {
            options.first = true;
        }
        this.runningRequestsNum_++;

        /**
         * @event GIS.Map.Layer.OL3.Vector#before.load
         * @type {{extent: number[], resolution: number, projection: string, first: boolean}}
         */
        this.emit("before.load", options);
    },
    /**
     * @param [options]
     * @fires GIS.Map.Layer.OL3.Vector#load
     * @protected
     */
    async onLoad(options = {}) {
        if (options.data) {
            options.features = await this.read(options.data, true);

            /**
             * @event GIS.Map.Layer.OL3.Vector#after.load
             * @type {{extent: number[], resolution: number, projection: string, last: boolean,
             *         response: Object, features: ol.Feature[]}}
             */
            this.emit('load', options);
        }
    },
    /**
     * @fires GIS.Map.Layer.OL3.Vector#after.load
     * @param {Object} [options]
     * @protected
     */
    afterLoad(options = {})
    {
        this.runningRequestsNum_--;
        if (!this.runningRequestsNum_) {
            options.last = true;
        }

        /**
         * @event GIS.Map.Layer.OL3.Vector#after.load
         * @type {{extent: number[], resolution: number, projection: string, last: boolean}}
         */
        this.emit("after.load", options);
    },
    /**
     * @param {Object=} options
     * @returns {ol.layer.Vector|ol.layer.Image}
     * @protected
     */
    createLayer(options)
    {
        let layer;

        const layerOptions = {
            ...Util.pickOptions(
                options,
                ["opacity", "extent", "minResolution", "maxResolution"]
            ),
            visible: !!options.visibility,
            source: this.createSource(options)
        };

        if (options.overlay) {
            layerOptions.map = this.map.getVendor();
        }

        layer = new VectorLayer(_.assign(layerOptions, {
            style: options.styleFunction,
            renderMode: options.asImage ? 'image' : 'vector',
        }))

        return layer;
    },
    /**
     * @param {Object} options
     * @returns {ol.source.ImageVector|ol.source.Vector}
     * @protected
     */
    createSource(options)
    {
        const sourceOptions = Util.pickOptions(options, ["projection", "wrapX", "attributions", "url",
                                                         "strategy", "format", "loader", "ratio"]);

        if (sourceOptions.format != null) {
            const formatOptions = {
                ...Util.pickOptions(options, ["geometryName", "dataGeometryName", "dataPrimaryKeyName"]),
                defaultDataProjection: this.projection
            };

            sourceOptions.format = this.createFormat(sourceOptions.format, formatOptions);
        }

        if (!_.isEmpty(sourceOptions.attributions)) {
            sourceOptions.attributions = this.Class.createAttributions(sourceOptions.attributions);
        }

        if (sourceOptions.url != null) {
            sourceOptions.url = this.Class.prepareURL(sourceOptions.url, options, options.urlParams);
        }

        if (sourceOptions.loader != null) {
            sourceOptions.loader = ((loader) => {
                return (
                    /**
                     * @param {ol.Extent} extent
                     * @param {number} resolution
                     * @param {ol.proj.Projection} projection
                     */
                    async (extent, resolution, projection) => {
                        const loaderOptions = {
                            extent,
                            resolution,
                            projection: projection.getCode()
                        };
                        try {
                            await Promise.resolve(this.beforeLoad(loaderOptions));

                            const key = "featuresLoad:" + Util.String.hash(JSON.stringify(loaderOptions));
                            this.cancelPromise(key);

                            const load = async arg => {
                                let data = await this.addPromise(key, arg)
                                if (data) {
                                    await Promise.resolve(this.onLoad(_.assign({}, loaderOptions, { data })))
                                }
                                return data
                            }

                            let ret = loader(loaderOptions)
                            if (isPromise(ret)) {
                                await load(ret)
                            } else if (isGenerator(ret)) {
                                let gen = ret
                                // load first value from generator
                                let data = await load(gen.next().value)
                                // try to load other values from generator
                                if (data && Array.isArray(data.items)) {
                                    while (true) {
                                        let v = gen.next(data)
                                        data = await load(v.value)
                                        if (v.done) {
                                            break
                                        }
                                    }
                                }
                            }
                        } catch (err) {
                            console.warn(err.stack);
                        } finally {
                            this.afterLoad(loaderOptions);
                        }
                    })
            })(sourceOptions.loader)
        }

        if (options.loadingStrategy != null) {
            sourceOptions.strategy = this.prepareLoadingStrategy(
                options.loadingStrategy,
                Util.pickOptions(options, ['maxZoom', 'minZoom', 'tileSize'])
            );
        }

        sourceOptions.features = new Collection();

        let source = this.source = new VectorSource(_.omit(sourceOptions, [ "ratio" ]));

        // if use clustering then wrap vector source by cluster source
        // todo: из-за этого куска не прокидываются события на выделение фечеров т.к. происк происходит не в том соурсе
        // if (options.clusterDistance != null) {
        //     source = this.clusterSource = new ol.source.Cluster({
        //         ..._.pick(
        //             sourceOptions,
        //             ["attributions", "format", "projection"]
        //         ),
        //         source,
        //         distance: parseFloat(options.clusterDistance),
        //     });
        // }

        source.on("addfeature", this.onSourceAddFeature, this);
        source.on("removefeature", this.onSourceRemoveFeature, this);

        return source;
    },
    /**
     * @param projection
     * @protected
     */
    setVendorProjection(projection)
    {
        // ol3 doesn't exports setProjection method in current version then recreate source of the layer
        if (this.layer) {
            if (this.source) {
                this.source.un("addfeature", this.onSourceAddFeature, this);
                this.source.un("removefeature", this.onSourceRemoveFeature, this);
            }

            if (this.clusterSource) {
                this.clusterSource.un("addfeature", this.onSourceAddFeature, this);
                this.clusterSource.un("removefeature", this.onSourceRemoveFeature, this);
            }

            this.layer.setSource(
                this.createSource({
                    ...Util.pickOptions(this, this.optionsNames()),
                    projection
                })
            );
        }
    },
    /**
     * @type {number|string} type
     * @type {Object} [options]
     * @returns {ol.format.Feature}
     * @protected
     */
    createFormat(type, options)
    {
        let format;

        const check = (value, type) => (Util.isNumeric(value) ?
                                        _.parseInt(value) === type :
                                        value === GIS.Format.name(type));

        switch (true) {
            case check(type, GIS.Format.GlonassJSON):
                format = new GlonassJSONFormat(options);
                break;
            case check(type, GIS.Format.GeoJSON):
                format = new GeoJSONFormat(options);
                break;
            case check(type, GIS.Format.GML2):
                format = new WFSFormat({
                    gmlFormat: new GML2Format(options)
                });
                break;
            case check(type, GIS.Format.GML3):
                format = new WFSFormat({
                    gmlFormat: new GML3Format(options)
                });
                break;
        }

        this.format_ = format;

        return format;
    },
    /**
     * @param {glStyleHash} style
     * @param {string} [cacheKey=this.name]
     * @returns {ol.style.StyleFunction}
     * @protected
     */
    createStyleFunction(style, cacheKey = this.name)
    {
        return this.map.createStyleFunction(style, cacheKey);
    },
    /**
     * @returns {Array.<ol.Feature>}
     */
    getFeatures()
    {
        return this.source.getFeatures();
    },
    /**
     * @returns {ol.Collection.<ol.Feature>}
     */
    getFeaturesCollection()
    {
        return this.source.getFeaturesCollection();
    },
    /**
     * @param {number|string} id
     * @returns {ol.Feature|undefined}
     */
    getFeature(id)
    {
        return this.findFeature(matchIdFilter(id));
    },
    /**
     * @returns {GIS.Map.Layer.OL3.Vector}
     */
    clear()
    {
        this.cancelAllPromises()
        this.source.clear();

        return this;
    },
    /**
     * Creates new item and adds it to the layer.
     * if `geog` is a GeoJSON geometry than will be created new feature
     * with that geometry, or if `geog` an instance of ol.Feature that that feature will be used as item feature.
     *
     * @fires GIS.Map.Layer.OL3.Vector#feature.insert
     * @param {Object|ol.Feature} geog
     * @param {Object} [data]
     * @returns {ol.Feature}
     */
    insert(geog, data = {})
    {
        let feature;

        if (geog instanceof Feature) {
            feature = geog;
        } else {
            data = {
                ...data,
                geometryName: this.geometryName,
                [ this.dataGeometryName ]: geog,
                [ this.geometryName ]: this.map.geomFromGeoJSON(geog)
            };

            feature = new Feature(data);
        }

        feature.setId(Util.coalesce(feature.getId(), feature.get(this.dataPrimaryKeyName), uuid.v4()));

        if (!this.source.getFeatureById(feature.getId())) {
            this.source.addFeature(feature);
        }

        return feature;
    },
    /**
     * @fires GIS.Map.Layer.OL3.Vector#feature.update
     * @param {(number|string)} id
     * @param {Object} data
     * @returns {ol.Feature}
     */
    update(id, data)
    {
        const feature = this.findFeature(matchIdFilter(id));

        if (feature) {
            feature.setProperties(data);
            feature.setId(Util.coalesce(feature.getId(), data[this.dataPrimaryKeyName], uuid.v4()));

            if (data[this.dataGeometryName]) {
                feature.setGeometry(this.map.geomFromGeoJSON(data[this.dataGeometryName]));
            }
            /**
             * @event GIS.Map.Layer.OL3.Vector#feature.update
             * @type {{feature: ol.Feature}}
             */
            this.emit("feature.update", { feature });
        }

        return feature;
    },
    /**
     * @fires GIS.Map.Layer.OL3.Vector#feature.remove
     * @param {(number|string|ol.Feature)} id
     * @returns {ol.Feature}
     */
    remove(id)
    {
        const feature = id instanceof Feature ? id : this.findFeature(matchIdFilter(id));

        if (feature && this.source.getFeatureById(feature.getId())) {
            this.source.removeFeature(feature);
        }

        return feature;
    },
    /**
     * @param {Object} data Data to parse
     * @param {boolean} [async=false]
     * @returns {Promise<ol.Feature[]>}
     */
    read(data, async = false)
    {
        // todo implement timed async process
        const features = this.format_.readFeatures(data, {
            featureProjection: this.map.getProjection(),
            dataProjection: this.projection
        });

        return new Promise(resolve => {
            if (async) {
                Util.Array.timedChunk(features, function (feature) {
                    if (!feature || !this.source) {
                        return;
                    }

                    this.source.addFeature(feature);
                }, this, () => resolve(features));
            } else {
                this.source.addFeatures(features);
                resolve(features);
            }
        });
    },
    /**
     * @param {glStyleHash} styleHash New style hash
     * @param {boolean} [merge=true] Merge with existing style hash or overwrite it
     * @returns {GIS.Map.Layer.OL3.Vector}
     */
    setStyle(styleHash, merge = true)
    {
        if (merge) {
            _.merge(this.style, styleHash);
        } else {
            this.style = styleHash;
        }

        this.styleFunction = _.throttle(this.createStyleFunction(this.style), 1000 / 30)

        if (this.asImage) {
            this.layer.getSource().setStyle(this.styleFunction);
        } else {
            this.layer.setStyle(this.styleFunction);
        }

        this.refresh();

        return this;
    },
    /**
     * @returns {glStyleHash}
     */
    getStyle()
    {
        return this.style;
    },
    /**
     * @param {string|string[]} path Style option path
     * @param {*} value
     * @returns {GIS.Map.Layer.OL3.Vector}
     */
    setStyleOption(path, value)
    {
        return this.setStyle(_.set({}, path, value));
    },
    /**
     * @param {string|string[]} path Style option path
     * @returns {*} Returns style option value at specified path
     */
    getStyleOption(path)
    {
        return _.get(this.style, path);
    },
    /**
     * @returns {ol.Extent|number[]}
     */
    getExtent()
    {
        return this.source.getExtent();
    },
    /**
     * @param {number[]} pixel
     * @param {function} iterator
     * @param {*} [context]
     * @returns {*}
     */
    forEachFeatureAtPixel(pixel, iterator, context = this)
    {
        return this.map.forEachFeatureAtPixel(pixel, (feature, layer) => {
            if (layer && layer === this.layer || this.getFeature(feature.getId())) {
                return Util.call(iterator, context, [feature, layer]);
            }
        });
    },
    /**
     * @param {function|ol.Feature} predicate
     * @param {*} [context]
     * @returns {ol.Feature|undefined}
     */
    findFeature(predicate, context = this)
    {
        return _.find(this.getFeatures(), predicate, context);
    },
    /**
     * @param {function} predicate
     * @param {*} [context]
     * @returns {ol.Feature[]}
     */
    findFeatures(predicate, context = this)
    {
        return _.filter(this.getFeatures(), predicate, context);
    },
    /**
     * @param {ol.Feature} feature
     * @returns {boolean}
     */
    isClusterFeature(feature) {
        return this.map.isClusterFeature(feature);
    },
    /**
     * @param {ol.source.Vector.Event} e
     * @protected
     */
    onSourceAddFeature(e)
    {
        var feature = e.feature;

        const bind = (feature) => {
            feature.glChangeKey_ = feature.on("change", _.throttle(this.onFeatureChange, 300), this);
            feature.setProperties({
                geometryName: this.geometryName,
                dataPrimaryKeyName: this.dataPrimaryKeyName,
                dataGeometryName: this.dataGeometryName,
                [ this.dataGeometryName ]: this.map.geomToGeoJSON(feature.getGeometry())
            }, true);

            if (!feature.getId()) {
                feature.setId(uuid.v4());
            }
            /**
             * @event GIS.Map.Layer.Vector#feature.insert
             * @type {{feature: ol.Feature}}
             */
            this.emit("feature.insert", { feature });
        };

        // prepare feature if it is cluster
        if (this.isClusterFeature(feature)) {
            const cluster = feature.get("features");

            if (cluster.length > 1) {
                feature.setProperties({
                    id: uuid.v4(),
                    clusterSize: cluster.length,
                    clusterExtent: this.map.clusterExtent(feature, this.map.getProjection(), this.projection)
                }, true);

                this.setFeatureStyleName(feature, "cluster");

                cluster.forEach(bind);
            } else {
                // if single element cluster replace it with feature itself
                this.clusterSource.removeFeature(feature);
                feature = cluster[0].clone();
                feature.setId(cluster[0].getId());
                this.clusterSource.addFeature(feature);
            }
        } else {
            bind(feature);
        }
    },
    /**
     * @param {ol.source.Vector.Event} e
     * @protected
     */
    onSourceRemoveFeature(e)
    {
        const feature = e.feature;

        const unbind = (feature) => {
            if (feature.glChangeKey_) {
                feature.glChangeKey_.listener.cancel();
                Observable.unByKey(feature.glChangeKey_);
                delete feature.glChangeKey_;
            }

            /**
             * @event GIS.Map.Layer.OL3.Vector#feature.remove
             * @type {{feature: feature}}
             */
            this.emit("feature.remove", { feature });
        };

        this.unselectFeature(feature);

        if (this.isClusterFeature(feature)) {
            feature.get("features").forEach(unbind);
        } else {
            unbind(feature);
        }
    },
    /**
     * @param {ol.Object.Event} e
     * @protected
     * @method
     */
    onFeatureChange(e)
    {
        const feature = (/** @type {ol.Feature} */e.target);

        feature.set(this.dataGeometryName, this.map.geomToGeoJSON(feature.getGeometry()), true);
    },
    /**
     * @param {number|string|ol.Feature} feature
     * @param {string} [styleName=default]
     * @param {string|undefined} [prevStyleName]
     * @returns {GIS.Map.Layer.OL3.Vector}
     */
    setFeatureStyleName (feature, styleName = 'default', prevStyleName = undefined)
    {
        feature = feature instanceof Feature ? feature : this.findFeature(matchIdFilter(feature))
        feature && this.map.setFeatureStyleName(feature, styleName, prevStyleName)

        return this
    },
    /**
     * @param {number|string|ol.Feature} feature
     * @return {string}
     */
    getFeatureStyleName (feature) {
        feature = feature instanceof Feature ? feature : this.findFeature(matchIdFilter(feature))

        return feature && this.map.getFeatureStyleName(feature)
    },
    /**
     * @param {ol.Feature} feature
     * @returns {GIS.Map.Layer.OL3.Vector}
     */
    selectFeature(feature)
    {
        this.map.selectFeature(feature);

        return this;
    },
    /**
     * @param {ol.Feature} feature
     * @returns {GIS.Map.Layer.OL3.Vector}
     */
    unselectFeature(feature)
    {
        this.map.unselectFeature(feature);

        return this;
    },
    isHitLayer(pixel) {
      return this.ParentCall(pixel);
    },
    /**
     * @param {{pixel: number[], coordinate: number[]}} e
     * @fires GIS.Map.Layer.OL3.Vector#click Fires if event occurred somewhere on layer.
     * @fires GIS.Map.Layer.OL3.Vector#feature.click Fires for each item at event pixel. If feature is cluster then fires for each part of cluster.
     * @protected
     */
    onMapClick(e)
    {
        if (this.isHitLayer(e.pixel)) {
            const feature = this.forEachFeatureAtPixel(e.pixel, (feature) => {
                if (this.isClusterFeature(feature) && feature.get("clusterSize") > 1 && this.map.getZoom() < this.map.maxZoom) {
                    this.map.fit(this.map.transformExtentProjection(feature.get("clusterExtent"), this.projection, this.map.getProjection()));
                } else {
                    return feature;
                }
            });

            if (feature) {
                e.feature = feature;
                /**
                 * @event GIS.Map.Layer.OL3.Vector#feature.click
                 * @type {{pixel: number[], coordinate: number[], feature: ol.Feature}}
                 */
                this.emit("feature.click", e);
            }

            return true;
        }
    },
    /** @param {ol.Collection.Event} e */
    onFeatureSelect(e)
    {
        if (this.findFeature && !!this.findFeature(e.feature)) {
            this.emit("feature.select", e);
        }
    },
    /** @param {ol.Collection.Event} e */
    onFeatureUnSelect(e)
    {
        if (this.findFeature && !!this.findFeature(e.feature)) {
            this.emit("feature.unselect", e);
        }
    },
    /**
     * @param {number[]} coordinate
     * @param {Object} [options]
     * @returns {Promise}
     */
    loadFeatureData(coordinate, options = {})
    {
        let feature = options.feature || this.getFeaturesAtCoordinate(coordinate)[0];
        if (!feature) {
            return Promise.reject(new RuntimeError('Feature not found at coordinate: ' + coordinate.join(',')));
        }

        if (this.isClusterFeature(feature)) {
            feature = feature.get("features")[0];
        }

        return Promise.resolve(this.map.pickFeatureProperties(feature));
    },
    /**
     * @param {number[]} coordinate
     * @returns {ol.Feature|undefined}
     */
    getClosestFeatureToCoordinate(coordinate)
    {
        return this.source.getClosestFeatureToCoordinate(coordinate);
    },
    /**
     * @param {number[]} coordinate
     * @return {Array.<ol.Feature>}
     */
    getFeaturesAtCoordinate (coordinate)
    {
        return this.source.getFeaturesAtCoordinate(coordinate)
    },
    /**
     * @inheritDoc
     */
    show() {
        this.layer.setZIndex(1);
        this.map.on("feature.select", this.onFeatureSelect, this)
            .on("feature.unselect", this.onFeatureUnSelect, this);

        return this.ParentCall();
    },
    /**
     * @inheritDoc
     */
    hide() {
        this.map.off("feature.select", this.onFeatureSelect, this)
            .off("feature.unselect", this.onFeatureUnSelect, this);

        return this.ParentCall();
    },
    /**
     * @param {Object | undefined} filter
     */
    filter (filter) {
        this.ParentCall()

        const matches = _.isEmpty(filter) ? _.constant(true) : _.matches(filter)

        this.getFeatures()
            .forEach(feature => {
                const featureData = this.map.pickFeatureProperties(feature)

                if (matches(featureData)) {
                    // если объект отвечает фильтру то восстанавливаем его стиль на начальный
                    this.setFeatureStyleName(feature, this.map.getFeaturePrevStyleName(feature), this.map.getFeaturePrevStyleName(feature))
                } else {
                    // иначе скрываем его
                    this.setFeatureStyleName(feature, 'remove')
                }
            })
    },
    /**
     * Force reload data of the source when used with XHR loader,
     * but saves already existent features.
     */
    reload () {
        const features = this.getFeatures().slice()

        this.clear()
        this.source.addFeatures(features)
        this.refresh()
    }
});

/**
 * @param {number|string} id
 * @returns {function(ol.Feature)}
 */
function matchIdFilter(id)
{
    return function (feature) {
        if (GIS.Map.OL3.isClusterFeature(feature)) {
            return _.some(feature.get("features"), matchIdFilter(id));
        }

        return feature.getId() === id;
    };
}

/**
 * @param {ol.Feature} sampleFeature
 * @returns {function(ol.Feature)}
 */
function matchFeatureFilter(sampleFeature)
{
    return function (feature) {
        if (GIS.Map.OL3.isClusterFeature(feature)) {
            return _.some(feature, matchFeatureFilter(sampleFeature));
        }
        return feature === sampleFeature;
    };
}

/**
 * @param {ol.Feature} sampleFeature
 * @param {number|string} [id]
 * @returns {function(ol.Feature)}
 */
function matchIdAndFeatureFilter(sampleFeature, id)
{
    const idFilter = matchIdFilter(id || sampleFeature.getId())
    const featureFilter = matchFeatureFilter(sampleFeature)

    return function (feature) {
        return idFilter(feature) && featureFilter(feature)
    }
}
